import { Logger, TLogger } from 'components/logger/Logger';
import { StateManager } from 'components/stateManager/StateManager';
import {
  IVideoControllerComponentsCommonProps,
  IVideoElementControllerProps,
} from 'components/videoElementController/IVideoElementController';
import {
  EVideoElementControllerBusEvent,
  TVideoControllerEventBus,
} from 'components/videoElementController/VideoElementController.events';
import { EPlaybackState, IPlayerState } from 'types/common';
import { bind } from 'utils/decorators/bind';

export class StateController {
  private _config: IVideoElementControllerProps = {};
  private _logger: TLogger;
  private _eventsMap = {
    [EVideoElementControllerBusEvent.PLAYING]: this._handlePlaying,
    [EVideoElementControllerBusEvent.LOADING]: this._handleLoading,
    [EVideoElementControllerBusEvent.VIDEO_PAUSE]: this._handlePause,
    [EVideoElementControllerBusEvent.CAN_PLAY]: this._handleCanPlay,
    [EVideoElementControllerBusEvent.PAUSED]: this._handlePaused,
    [EVideoElementControllerBusEvent.RESTART]: this._handleRestart,
    [EVideoElementControllerBusEvent.UPDATED]: this._handleUpdate,
    [EVideoElementControllerBusEvent.STALLED]: this._handleStalled,
    [EVideoElementControllerBusEvent.WAITING]: this._handleWaiting,
    [EVideoElementControllerBusEvent.UPDATE_PLAYER_RESOLUTION]: this._handleUpdatePlayerResolution,
    [EVideoElementControllerBusEvent.ERROR]: this._handleError,
    [EVideoElementControllerBusEvent.SET_VOLUME]: this._handleSetVolume,
    [EVideoElementControllerBusEvent.DESTROY]: this.destroy,
  };

  private readonly _state: StateManager<IPlayerState>;
  private readonly _eventBus: TVideoControllerEventBus;

  constructor(props: IVideoControllerComponentsCommonProps) {
    this._state = props.state;
    this._eventBus = props.eventBus;
    this._logger = Logger(this._eventBus, 'VideoElementController - StateController');

    this._logger.info('Created');
    this._logger.verbose('Initial state', props.state);

    this.init();
  }

  public setConfig(config: IVideoElementControllerProps) {
    this._logger.debug('Config update received, the new one:', config);
    this._config = config;
  }

  @bind
  public destroy() {
    this._logger.info('Destroy');
    this._eventBus.toggleSubscriptionBulk({
      action: 'unsubscribe',
      callbacks: this._eventsMap,
    });
    this._state.resetState();
    this._state.setState({
      isDestroyed: true,
    });
  }

  @bind
  public init() {
    this._logger.info('Initialization requested, attaching eventBus listeners');
    this._eventBus.toggleSubscriptionBulk({
      action: 'subscribe',
      callbacks: this._eventsMap,
    });
  }

  @bind
  private _updateState(newState: Partial<IPlayerState>) {
    const prevPlaybackState = this._state.getState().videoPlaybackState;
    let nextPlaybackState = prevPlaybackState;

    // TODO: Set PlaybackState directly instead of using boolean flags.
    if (newState.isPlaying) {
      nextPlaybackState = EPlaybackState.PLAYING;
    } else if (newState.isLoading) {
      nextPlaybackState = EPlaybackState.LOADING;
    } else if (newState.isPaused) {
      nextPlaybackState = EPlaybackState.PAUSED;
    } else if (newState.isError) {
      nextPlaybackState = EPlaybackState.ERROR;
    }

    this._logger.debug(`State updated: ${prevPlaybackState} -> ${nextPlaybackState}`);

    this._state.setState({
      ...newState,
      videoPlaybackState: nextPlaybackState,
    });

    if (prevPlaybackState !== nextPlaybackState) {
      this._eventBus.publish(EVideoElementControllerBusEvent.VIDEO_PLAYBACK_STATE_CHANGED, {
        prevState: prevPlaybackState,
        nextState: nextPlaybackState,
      });
    }
  }

  @bind
  private _handlePlaying() {
    this._logger.debug('Playing event received');
    this._updateState({
      isPlaying: true,
      startedPlaying: true,
      isLoading: false,
      isStalled: false,
      isWaiting: false,
      isPaused: false,
      isError: false,
    });
  }

  @bind
  private _handleLoading() {
    this._logger.debug('Loading event received');
    this._updateState({ isLoading: true, isPlaying: false });
  }

  @bind
  private _handlePause() {
    this._logger.debug('Pause event received');
    this._updateState({ isPlaying: false, isPaused: true });
  }

  @bind
  private _handlePaused() {
    this._logger.debug('Paused event received');
    this._updateState({ isPlaying: false, isLoading: false, isPaused: true });
  }

  @bind
  private _handleCanPlay() {
    this._logger.debug('CanPlay event received');
    this._updateState({ canPlay: true, isDestroyed: false });
  }

  @bind
  private _handleRestart() {
    this._logger.debug('Restart event received');
    this._updateState({ isRestarted: true });
  }

  @bind
  private _handleStalled() {
    this._logger.debug('Stalled event received');
    this._updateState({ isStalled: true });
  }

  @bind
  private _handleWaiting() {
    this._logger.debug('Waiting event received');
    this._updateState({ isWaiting: true });
  }

  @bind
  private _handleError() {
    this._logger.debug('Error event received');
    this._updateState({ isError: true });
  }

  @bind
  private _handleSetVolume({ volume }: { volume: number }) {
    this._logger.debug('Volume event received', { volume });
    this._updateState({ volume });
  }

  @bind
  private _handleUpdatePlayerResolution(e: { height: number; width: number }) {
    this._logger.debug('Player resolution event received', { height: e.height, width: e.width });
    this._updateState({
      videoElementResolution: { height: e.height, width: e.width },
    });
  }

  @bind private _handleUpdate() {
    this._logger.debug('Update event received');
    this._updateState({
      canPlay: false,
      isLoading: true,
      isPlaying: false,
      isRestarted: false,
      isPaused: false,
      isError: false,
    });
  }
}
